import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Loader2, ExternalLink, ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight,
  ListChecks, CheckCircle2, Sparkles,
} from "lucide-react";

const DIRECTION_META = {
  pull: { label: "Pull", icon: ArrowDownToLine, color: "text-emerald-700", bg: "bg-emerald-50" },
  push: { label: "Push", icon: ArrowUpFromLine, color: "text-[#1877F2]", bg: "bg-[#1877F2]/10" },
  both: { label: "2-way", icon: ArrowLeftRight, color: "text-purple-700", bg: "bg-purple-50" },
};

/**
 * Catalogue + admin wishlist/connected checklist for an integration.
 *
 * Embed once per integration page:
 *   <IntegrationCapabilities integrationKey="xero" accent="#13B5EA" />
 *
 * Source of truth = backend `CATALOGUES` map. Admin state (wishlist,
 * connected, notes) is persisted server-side so it survives sessions.
 */
export default function IntegrationCapabilities({ integrationKey, accent = "#1877F2" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // "", "wishlist", "connected", "todo"
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/integration-capabilities/${integrationKey}`);
      setData(resp);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load capabilities");
    } finally { setLoading(false); }
  }, [integrationKey]);
  useEffect(() => { load(); }, [load]);

  const patch = async (capabilityId, body) => {
    setSavingId(capabilityId);
    try {
      await api.patch(
        `/integration-capabilities/${integrationKey}/${capabilityId}`, body);
      // Optimistic local update so the UI doesn't flash on every tick.
      setData((prev) => prev ? {
        ...prev,
        capabilities: prev.capabilities.map((c) =>
          c.id === capabilityId ? { ...c, ...body } : c),
        summary: {
          ...prev.summary,
          wishlist: prev.capabilities.reduce(
            (n, c) => n + (c.id === capabilityId
              ? (body.wishlist !== undefined ? Number(body.wishlist) : Number(c.wishlist))
              : Number(c.wishlist)), 0),
          connected: prev.capabilities.reduce(
            (n, c) => n + (c.id === capabilityId
              ? (body.connected !== undefined ? Number(body.connected) : Number(c.connected))
              : Number(c.connected)), 0),
        },
      } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      load();
    } finally { setSavingId(null); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "wishlist") return data.capabilities.filter((c) => c.wishlist);
    if (filter === "connected") return data.capabilities.filter((c) => c.connected);
    if (filter === "todo") return data.capabilities.filter((c) => c.wishlist && !c.connected);
    return data.capabilities;
  }, [data, filter]);

  // Group by category (preserve catalogue order within a group).
  const grouped = useMemo(() => {
    const m = {};
    for (const c of filtered) {
      (m[c.category || "Other"] = m[c.category || "Other"] || []).push(c);
    }
    return m;
  }, [filtered]);

  if (loading) {
    return (
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 text-center text-zinc-500">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { summary } = data;

  return (
    <Card className="rounded-sm border-zinc-200" data-testid={`capabilities-${integrationKey}`}>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <ListChecks className="h-4 w-4" style={{ color: accent }} />
              API capabilities
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200">
                {summary.connected}/{summary.total} connected
              </Badge>
              {summary.wishlist > 0 && (
                <Badge className="rounded-sm bg-amber-100 text-amber-800 border-amber-200">
                  <Sparkles className="h-3 w-3 mr-1" /> {summary.wishlist} wishlist
                </Badge>
              )}
            </div>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Every API call this provider supports. Tick <strong>Wishlist</strong> for things
              you plan to enable, <strong>Connected</strong> for things already live in our app.
              The list survives reloads and is admin-shared.
            </p>
          </div>
          {data.docs_url && (
            <a href={data.docs_url} target="_blank" rel="noreferrer"
              className="text-xs inline-flex items-center gap-1 hover:underline"
              style={{ color: accent }}
              data-testid={`capabilities-${integrationKey}-docs`}>
              Provider docs <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          {[
            { v: "", label: `All (${summary.total})` },
            { v: "connected", label: `Connected (${summary.connected})` },
            { v: "wishlist", label: `Wishlist (${summary.wishlist})` },
            { v: "todo", label: "To-do (wishlist & not connected)" },
          ].map((f) => (
            <button key={f.v} type="button" onClick={() => setFilter(f.v)}
              className={`px-2.5 h-7 rounded-sm border font-mono uppercase tracking-[0.12em] transition-colors ${
                filter === f.v
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`capabilities-${integrationKey}-filter-${f.v || "all"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-sm italic text-zinc-500 py-8 text-center"
            data-testid={`capabilities-${integrationKey}-empty`}>
            Nothing in this view.
          </div>
        ) : (
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} data-testid={`capabilities-${integrationKey}-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="label-overline mb-2">{cat}</div>
              <div className="border border-zinc-200 rounded-sm overflow-hidden">
                {items.map((c, i) => {
                  const dir = DIRECTION_META[c.direction] || DIRECTION_META.pull;
                  const Icon = dir.icon;
                  return (
                    <div key={c.id}
                      className={`grid grid-cols-[auto_1fr_auto_auto] gap-3 px-3 py-2.5 items-start ${
                        i > 0 ? "border-t border-zinc-100" : ""
                      } ${c.connected ? "bg-emerald-50/30" : ""}`}
                      data-testid={`capabilities-${integrationKey}-row-${c.id}`}>
                      <div className={`h-6 w-6 grid place-items-center rounded-sm ${dir.bg}`}>
                        <Icon className={`h-3.5 w-3.5 ${dir.color}`} title={dir.label} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display font-bold text-sm">{c.label}</span>
                          <code className="font-mono text-[10px] text-zinc-500 bg-zinc-50 px-1.5 py-0.5 rounded-sm">
                            {c.id}
                          </code>
                          {c.connected && (
                            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 text-[9px]">
                              <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> live
                            </Badge>
                          )}
                          {c.wishlist && !c.connected && (
                            <Badge className="rounded-sm bg-amber-100 text-amber-800 border-amber-200 text-[9px]">
                              <Sparkles className="h-2.5 w-2.5 mr-0.5" /> wishlist
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-600 mt-0.5 leading-snug">{c.description}</p>
                        <NotesField cap={c} integrationKey={integrationKey} onSave={(notes) => patch(c.id, { notes })} />
                      </div>
                      <label className="flex items-center gap-1.5 text-[11px] text-zinc-700 cursor-pointer pt-0.5">
                        <Checkbox checked={c.wishlist}
                          onCheckedChange={(v) => patch(c.id, { wishlist: !!v })}
                          disabled={savingId === c.id}
                          data-testid={`capabilities-${integrationKey}-${c.id}-wishlist`} />
                        <span>Wishlist</span>
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] text-zinc-700 cursor-pointer pt-0.5">
                        <Checkbox checked={c.connected}
                          onCheckedChange={(v) => patch(c.id, { connected: !!v })}
                          disabled={savingId === c.id}
                          data-testid={`capabilities-${integrationKey}-${c.id}-connected`} />
                        <span>Connected</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function NotesField({ cap, integrationKey, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(cap.notes || "");
  useEffect(() => { setVal(cap.notes || ""); }, [cap.notes]);
  if (!editing && !cap.notes) {
    return (
      <button type="button" onClick={() => setEditing(true)}
        className="text-[10px] text-zinc-400 hover:text-zinc-700 mt-1 italic"
        data-testid={`capabilities-${integrationKey}-${cap.id}-add-note`}>
        + add note
      </button>
    );
  }
  if (!editing) {
    return (
      <div className="mt-1 text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm px-2 py-1 inline-flex items-center gap-2"
        data-testid={`capabilities-${integrationKey}-${cap.id}-note`}>
        <span className="italic">&ldquo;{cap.notes}&rdquo;</span>
        <button type="button" onClick={() => setEditing(true)}
          className="text-zinc-400 hover:text-zinc-900 underline text-[10px]">
          edit
        </button>
      </div>
    );
  }
  return (
    <div className="mt-1 flex gap-1 items-center">
      <Input value={val} onChange={(e) => setVal(e.target.value)}
        placeholder="Note (visible to other admins)" maxLength={500}
        className="h-7 text-[11px]"
        data-testid={`capabilities-${integrationKey}-${cap.id}-note-input`} />
      <button type="button"
        onClick={() => { onSave(val); setEditing(false); }}
        className="text-[10px] uppercase tracking-[0.12em] bg-zinc-900 text-white px-2 h-7 rounded-sm"
        data-testid={`capabilities-${integrationKey}-${cap.id}-note-save`}>save</button>
      <button type="button" onClick={() => { setVal(cap.notes || ""); setEditing(false); }}
        className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 px-1">cancel</button>
    </div>
  );
}
